import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { BackgroundColor } from "../constants/backgroundColors";
import { BACKGROUND_COLORS, DEFAULT_BACKGROUND_COLOR } from "../constants/backgroundColors";
import type { BackgroundPatternType } from "../constants/backgroundPatterns";
import { DEFAULT_BACKGROUND_PATTERN } from "../constants/backgroundPatterns";
import type { CardStyleId } from "../constants/cardStyles";
import { normalizeSortSelection } from "../constants/sortOptions";
import type { ViewFieldSettings, ViewLayout, ViewSettings } from "../types/viewSettings";
import { DEFAULT_VIEW_SETTINGS } from "../types/viewSettings";

/** 旧 backgroundPattern → 新 backgroundColor + backgroundPatternType マッピング */
const MIGRATION_MAP: Record<string, { color: BackgroundColor; pattern: BackgroundPatternType }> = {
  none: { color: "gray", pattern: "none" },
  dots: { color: "blue", pattern: "dots" },
  stripes: { color: "blue", pattern: "stripes" },
  grid: { color: "blue", pattern: "grid" },
  pink: { color: "pink", pattern: "waves" },
  yellow: { color: "yellow", pattern: "dots" },
};

interface ViewSettingsState extends ViewSettings {
  isLoaded: boolean;
  setLayout: (layout: ViewLayout) => void;
  setFieldVisibility: (field: keyof ViewFieldSettings, visible: boolean) => void;
  setShowPrivateMemo: (show: boolean) => void;
  setCardStyle: (style: CardStyleId) => void;
  setColorPickerAlwaysVisible: (visible: boolean) => void;
  setBackgroundColor: (color: BackgroundColor) => void;
  setBackgroundPatternType: (pattern: BackgroundPatternType) => void;
  resetBackgroundSettings: () => void;
  setSort: (sortBy: ViewSettings["sortBy"], sortOrder: ViewSettings["sortOrder"]) => void;
  updateSettings: (newSettings: Partial<ViewSettings>) => void;
  resetToDefaults: () => void;
  setMinimalView: () => void;
  setFullView: () => void;
}

export const useViewSettingsStore = create<ViewSettingsState>()(
  persist(
    (set) => ({
      // Initial state
      ...DEFAULT_VIEW_SETTINGS,
      isLoaded: true,

      // Actions
      setLayout: (layout: ViewLayout) => {
        let fields: ViewFieldSettings;

        switch (layout) {
          case "minimal":
            fields = {
              logo: true,
              provider: false,
              description: false,
              releaseDate: false,
              devCountry: false,
              updatedAt: false,
              tags: false,
              actions: true,
            };
            break;

          case "compact":
            fields = {
              logo: true,
              provider: true,
              description: false,
              releaseDate: false,
              devCountry: false,
              updatedAt: false,
              tags: false,
              actions: true,
            };
            break;

          case "full":
            fields = {
              logo: true,
              provider: true,
              description: true,
              releaseDate: true,
              devCountry: true,
              updatedAt: true,
              tags: true,
              actions: true,
            };
            break;
        }

        set((state) => ({
          ...state,
          layout,
          fields,
          forceUpdateAt: Date.now(),
        }));
      },

      setFieldVisibility: (field: keyof ViewFieldSettings, visible: boolean) => {
        set((state) => ({
          ...state,
          fields: {
            ...state.fields,
            [field]: visible,
            logo: true,
          },
          forceUpdateAt: Date.now(),
        }));
      },

      setShowPrivateMemo: (show: boolean) => {
        set((state) => ({
          ...state,
          showPrivateMemo: show,
          forceUpdateAt: Date.now(),
        }));
      },

      setCardStyle: (style: CardStyleId) => {
        set((state) => ({
          ...state,
          cardStyle: style,
          forceUpdateAt: Date.now(),
        }));
      },

      setColorPickerAlwaysVisible: (visible: boolean) => {
        set((state) => ({
          ...state,
          colorPickerAlwaysVisible: visible,
          forceUpdateAt: Date.now(),
        }));
      },

      setBackgroundColor: (color: BackgroundColor) => {
        set({ backgroundColor: color });
      },

      setBackgroundPatternType: (pattern: BackgroundPatternType) => {
        set({ backgroundPatternType: pattern });
      },

      resetBackgroundSettings: () => {
        set({
          backgroundColor: DEFAULT_BACKGROUND_COLOR,
          backgroundPatternType: DEFAULT_BACKGROUND_PATTERN,
        });
      },

      setSort: (sortBy, sortOrder) => {
        // 提供していない組み合わせを永続化させない（書き込み口での不変条件の強制）
        set(normalizeSortSelection(sortBy, sortOrder));
      },

      updateSettings: (newSettings: Partial<ViewSettings>) => {
        set((state) => ({
          ...state,
          ...newSettings,
          fields: newSettings.fields
            ? {
                ...state.fields,
                ...newSettings.fields,
                actions: true,
                logo: true,
              }
            : {
                ...state.fields,
                actions: true,
                logo: true,
              },
          forceUpdateAt: Date.now(),
        }));
      },

      resetToDefaults: () => {
        set({
          ...DEFAULT_VIEW_SETTINGS,
          forceUpdateAt: Date.now(),
          isLoaded: true,
        });
      },

      setMinimalView: () => {
        set((state) => ({
          ...state,
          fields: {
            logo: true,
            provider: true,
            description: false,
            releaseDate: false,
            devCountry: false,
            updatedAt: false,
            tags: false,
            actions: true,
          },
          forceUpdateAt: Date.now(),
        }));
      },

      setFullView: () => {
        set((state) => ({
          ...state,
          fields: {
            logo: true,
            provider: true,
            description: true,
            releaseDate: true,
            devCountry: true,
            updatedAt: true,
            tags: true,
            actions: true,
          },
          forceUpdateAt: Date.now(),
        }));
      },
    }),
    {
      name: "ai-deck-view-settings",
      version: 3,
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Record<string, unknown>;

        if (version === 0 || !("backgroundColor" in state)) {
          // v0 → v1: backgroundPattern を backgroundColor + backgroundPatternType に分割
          const oldPattern = (state.backgroundPattern as string) ?? "stripes";
          const migration = MIGRATION_MAP[oldPattern] ?? {
            color: DEFAULT_BACKGROUND_COLOR,
            pattern: DEFAULT_BACKGROUND_PATTERN,
          };

          state.backgroundColor = migration.color;
          state.backgroundPatternType = migration.pattern;
          delete state.backgroundPattern;
        }

        if (version < 2 || !("sortBy" in state)) {
          // v1 → v2: sortBy/sortOrder を追加（デフォルト: お気に入り数順・降順）
          state.sortBy = DEFAULT_VIEW_SETTINGS.sortBy;
          state.sortOrder = DEFAULT_VIEW_SETTINGS.sortOrder;
        }

        if (version < 3) {
          // v2 → v3: ソート UI を 4 択に集約。廃止した組み合わせは同キーの既定方向へ寄せる
          const normalized = normalizeSortSelection(state.sortBy, state.sortOrder);
          state.sortBy = normalized.sortBy;
          state.sortOrder = normalized.sortOrder;
        }

        return state as unknown as ViewSettingsState;
      },
      partialize: (state) =>
        ({
          layout: state.layout,
          fields: state.fields,
          density: state.density,
          showPrivateMemo: state.showPrivateMemo,
          cardStyle: state.cardStyle,
          colorPickerAlwaysVisible: state.colorPickerAlwaysVisible,
          backgroundColor: state.backgroundColor,
          backgroundPatternType: state.backgroundPatternType,
          sortBy: state.sortBy,
          sortOrder: state.sortOrder,
        }) as unknown as ViewSettingsState,
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // レイアウト "card" → "full" マイグレーション
        if (state.layout === ("card" as unknown)) {
          state.layout = "full";
        }
        // フォールバック: 新フィールドが存在しない場合
        if (!state.backgroundColor) {
          state.backgroundColor = DEFAULT_BACKGROUND_COLOR;
        }
        if (!state.backgroundPatternType) {
          state.backgroundPatternType = DEFAULT_BACKGROUND_PATTERN;
        }
        // migrate はバージョンが上がった時しか走らないため、欠損・不正値をここでも吸収する
        const normalizedSort = normalizeSortSelection(state.sortBy, state.sortOrder);
        state.sortBy = normalizedSort.sortBy;
        state.sortOrder = normalizedSort.sortOrder;
        // FOUC 対策: 初回ペイント前に html.dark を同期付与
        if (typeof document !== "undefined") {
          const isDark = BACKGROUND_COLORS[state.backgroundColor]?.isDark ?? false;
          document.documentElement.classList.toggle("dark", isDark);
        }
      },
    },
  ),
);
